import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Upload, FileText, Trash2, Download, LogOut, Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { API } from "../lib/api";

const Portal = () => {
  const { user, logout } = useAuth();
  const [files, setFiles] = useState([]);
  const [docType, setDocType] = useState("license");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/files/mine");
      setFiles(r.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!selected) { setMsg("Please choose a file first."); return; }
    setUploading(true);
    setMsg("");
    const fd = new FormData();
    fd.append("file", selected);
    fd.append("doc_type", docType);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSelected(null);
      setMsg("Document uploaded successfully.");
      document.getElementById("portal-file").value = "";
      load();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/files/${id}`);
    load();
  };

  const token = localStorage.getItem("ffd_token");

  return (
    <div className="dash">
      <div className="dash-top">
        <div className="container dash-top-row">
          <Link to="/" className="dash-brand">FLYING FLEET <span>DISPATCH</span></Link>
          <div className="dash-user">
            {user?.role === "admin" && <Link to="/admin" className="dash-link">Admin</Link>}
            <span className="dash-name">{user?.name}</span>
            <button className="dash-logout" onClick={logout}><LogOut size={16} /> Log out</button>
          </div>
        </div>
      </div>

      <div className="container dash-body">
        <div className="dash-head">
          <h1>My Documents</h1>
          <p>Upload your license and insurance documents so we can get you dispatched faster.</p>
        </div>

        <div className="portal-grid">
          <form className="upload-card" onSubmit={upload}>
            <h3><Upload size={18} /> Upload a document</h3>
            <div className="field">
              <label>Document type</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}>
                <option value="license">Driver's License / CDL</option>
                <option value="insurance">Insurance (COI)</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="field">
              <label>File (PDF or image, max 15MB)</label>
              <input id="portal-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.heic" onChange={(e) => setSelected(e.target.files[0])} />
            </div>
            <button type="submit" className="btn btn-primary btn-block" disabled={uploading}>
              {uploading ? <Loader2 size={16} className="spin" /> : <Upload size={16} />} Upload
            </button>
            {msg && <p className="upload-msg">{msg}</p>}
            <div className="upload-note"><ShieldCheck size={14} /> Stored securely. Only you and dispatch can view.</div>
          </form>

          <div className="files-card">
            <h3><FileText size={18} /> Your uploaded documents</h3>
            {files.length === 0 ? (
              <div className="files-empty">No documents yet. Upload your license and insurance to get started.</div>
            ) : (
              <ul className="file-list">
                {files.map((f) => (
                  <li key={f.id}>
                    <span className="file-ico"><FileText size={18} /></span>
                    <div className="file-meta">
                      <span className="file-name">{f.original_name}</span>
                      <span className="file-tag">{f.doc_type} · {(f.size / 1024).toFixed(0)} KB · {new Date(f.uploaded_at).toLocaleDateString()}</span>
                    </div>
                    <div className="file-actions">
                      <a href={`${API}/files/download/${f.id}?auth=${token}`} target="_blank" rel="noreferrer" className="file-btn" title="Download"><Download size={16} /></a>
                      <button className="file-btn danger" onClick={() => remove(f.id)} title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portal;
